/** @format */

/**
 * Unified Badge — ONE component with variants, the single source for every
 * status / type / priority / code chip across the system. Colours come from
 * design tokens (HUE_TOKEN) or an API hex (`color`, via hexToBadgeStyle) — never
 * raw literals. Replaces the scattered StatusBadge/CodeBadge/PriorityIndicator/
 * typeTag/registry-Pill implementations.
 */

import type { ReactNode } from 'react';
import { cn } from '@clarix/ui';
import { hexToBadgeStyle } from '@clarix/utils';
import { HUE_TOKEN, type Hue } from './data-table/engine/cells/hue';

export type BadgeVariant = 'soft' | 'solid' | 'outline' | 'dot';

/**
 * THE single hover affordance for every interactive chip / pill / icon-trigger
 * across all tables. One soft ring (status-badge style) sitting just outside the
 * element with a 1px gap — import this everywhere instead of bespoke
 * `hover:ring-*` / `hover:bg-*` so the hover border is identical system-wide.
 *
 * The ring offset colour is driven by `--chip-ring-offset-color` (see
 * styles.css): white on normal rows, the selection blue on selected rows — so
 * the ring's gap never shows a white halo over a selected (blue) row.
 */
export const HOVER_RING =
  'hover:ring-2 hover:ring-offset-2 hover:ring-info-light [--tw-ring-offset-color:var(--chip-ring-offset-color)] ' +
  // While the chip's picker popover is OPEN the pointer still sits on the
  // trigger, so `:hover` keeps the ring lit and the chip reads as a highlighted
  // border. `OptionPicker`'s trigger carries `group/picker` + `data-state`, so
  // switch the ring off for the duration.
  'group-data-[state=open]/picker:ring-0 group-data-[state=open]/picker:ring-offset-0';

/**
 * Icon-button hover — a subtle gray fill (matches the reference's
 * `.fieldact/.rowact button:hover{background:var(--gray-bg)}`), NOT a ring.
 */
export const HOVER_RING_TIGHT = 'hover:bg-muted hover:text-foreground';

export interface BadgeProps {
  children: ReactNode;
  /** soft (light bg + colored text — default), solid (filled), outline (border), dot (dot + label, no bg). */
  variant?: BadgeVariant;
  /** Token hue. Default 'gray'. Ignored when `color` (hex) is set. */
  tone?: Hue;
  /** API hex colour — overrides `tone` (the one dynamic exception). */
  color?: string | null;
  /** xs (default, compact) or sm (larger — used for the Status pill). */
  size?: 'xs' | 'sm';
  /** Corner radius — 'full' capsule (default), 'lg' (9px, Status pill), 'md' (6px), 'sm'. */
  radius?: 'full' | 'lg' | 'md' | 'sm';
  /** Leading dot (for soft/solid). */
  withDot?: boolean;
  /** Adds a light border on hover (for clickable/editable badges). */
  interactive?: boolean;
  className?: string;
}

export function Badge({
  children,
  variant = 'soft',
  tone = 'gray',
  color,
  size = 'xs',
  radius = 'full',
  withDot,
  interactive,
  className,
}: BadgeProps) {
  const t = HUE_TOKEN[tone] ?? HUE_TOKEN.gray;
  // Pill sizing — xs is the compact chip (Priority/Type/Stage); sm is the larger
  // Status pill from the reference.
  // Sizes mirror the reference: `.tag` (xs) = 2px 8px / 11px; `.pill` (sm) =
  // 3px 9px / 11.5px with a 6px gap.
  const pad =
    size === 'xs'
      ? 'px-2 py-0.5 text-sm leading-[1.4] gap-1'
      : 'px-[9px] py-[3px] text-[11.5px] leading-[1.4] gap-1.5';
  const rounded =
    radius === 'sm'
      ? 'rounded-sm'
      : radius === 'md'
        ? 'rounded-md'
        : radius === 'lg'
          ? 'rounded-[9px]'
          : 'rounded-full';
  const hoverBorder = interactive ? HOVER_RING : '';

  if (variant === 'dot') {
    // Reference `.prio` — 7px dot + colored bold label, no bg/padding (the
    // editable wrapper provides the hover ring).
    return (
      <span className={cn('inline-flex items-center gap-[7px] rounded-md font-medium', !color && t.fg, hoverBorder, className)}>
        <span
          className={cn('h-[7px] w-[7px] shrink-0 rounded-full', !color && t.dot)}
          style={color ? { backgroundColor: color } : undefined}
        />
        {children}
      </span>
    );
  }

  const variantCls =
    variant === 'solid'
      ? cn('text-white', !color && t.dot) // dot token doubles as the solid fill
      : variant === 'outline'
        ? cn('border', !color && t.fg, !color && 'border-current/30')
        : cn(!color && t.bg, !color && t.fg); // soft

  return (
    <span
      className={cn(
        // `shrink-0` — table flex cells otherwise compress the pill and clip its right edge.
        'inline-flex shrink-0 items-center whitespace-nowrap font-medium',
        rounded,
        pad,
        variantCls,
        hoverBorder,
        className,
      )}
      style={color && variant !== 'solid' ? hexToBadgeStyle(color) : color ? { backgroundColor: color } : undefined}
    >
      {withDot ? (
        <span
          className={cn('h-[7px] w-[7px] shrink-0 rounded', variant === 'solid' ? 'bg-white' : !color && t.dot)}
          style={color && variant !== 'solid' ? { backgroundColor: color } : undefined}
        />
      ) : null}
      {children}
    </span>
  );
}
